// DEPENDENCIES
import React, { useState, useEffect, useContext, useRef, useMemo } from "react";

// CSS
import "./PropertyConsoleEditAddDialog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import {
	getAPICall,
	postAPICall,
	putAPICall,
} from "../../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../../context/UserContext";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	ButtonGroup,
	CircularProgress,
	Collapse,
	debounce,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";

// MUI ICONS
import CloseIcon from "@mui/icons-material/Close";

// CUSTOM COMPONENTS
import DropboxFileSelector from "../../../Misc/DropboxFileSelector/DropboxFileSelector";

// CONSTANTS
import { generateAlphanumericCode } from "../../../../constants/constants";

// OTHER

// property/create and property/update respond with the raw mysql error
// object at HTTP 200 on a DB failure (no thrown status), so postAPICall
// resolves normally — this checks the body's shape to tell a mysql error
// packet (has code/errno/sqlMessage) apart from a real OkPacket result.
const isDbErrorResponse = (data) =>
	Boolean(data) &&
	(data.errno !== undefined || data.sqlMessage !== undefined || data.code !== undefined);

// IDs of the OIC custom field's dropdown values in BQE — same hardcoded mapping
// PendingJobReviewScreen uses when associating a newly-created BQE project's OIC.
const BQE_OIC_CUSTOM_FIELD_VALUES = {
	WA: "d5e226fe-5798-43a4-a752-7bcd087eeda3",
	OR: "03356b2a-8467-46ef-9ce7-cf4dc99599d3",
	UT: "60d08179-1541-41ee-938f-9415ba4b5872",
};

// Root Dropbox folder (state office) that a new property's folder is created
// under, based on its OIC — the user can still browse elsewhere below to override.
const OIC_DROPBOX_ROOT_PATHS = {
	WA: "/Projects/WA-SEA",
	OR: "/Projects/OR-PDX",
	UT: "/Projects/UT-SLC",
};

// REACT COMPONENT
const PropertyConsoleEditAddDialog = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// REFS
	const hubSpotPropertySearchByValueRef = useRef(null);
	const bqePropertyNameRef = useRef(null);
	// Tracks the OIC-derived path we last auto-filled, so the effect below can tell
	// "still whatever we defaulted it to" apart from "user browsed somewhere else" —
	// only the former should be overwritten when OIC changes again.
	const oicDefaultedDropboxPathRef = useRef("");

	// STATES
	const [loading, setLoading] = useState(true);
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");

	// HubSpot Property
	const [hubSpotPropertySearchByValue, setHubSpotPropertySearchByValue] =
		useState("");
	const [hubSpotPropertyOptions, setHubSpotPropertyOptions] = useState([]);
	const [hubSpotPropertySearchLoading, setHubSpotPropertySearchLoading] =
		useState(false);

	const [hubSpotPropertyRecord, setHubSpotPropertyRecord] = useState(null);

	// "search" reuses an existing HubSpot property record (original behavior);
	// "create" instead creates a brand-new record via /api/hubspot/addProperty
	// from the fields below. Editing always searches — the tools record is
	// already linked to a real HubSpot record, so there's nothing to create.
	const [hubspotMode, setHubspotMode] = useState("search");
	const [newHubspotProperty, setNewHubspotProperty] = useState({
		name: "",
		address1: "",
		address2: "",
		city: "",
		state: "",
		zip: "",
	});

	// BQE Property
	const [bqePropertyOptions, setBqePropertyOptions] = useState([]);
	const [bqePropertyDetailedViewOpen, setBqePropertyDetailedViewOpen] =
		useState(false);
	const [bqePropertySearchValue, setBqePropertySearchValue] = useState("");
	const [bqePropertySearchLoading, setBqePropertySearchLoading] =
		useState(false);

	const [bqePropertyRecord, setBqePropertyRecord] = useState(null);

	// "existing" reuses a BQE property found via search (original behavior); "none"
	// leaves the record unlinked to any BQE property; "create" creates a brand-new
	// BQE property (and, if needed, a brand-new BQE client) via /api/bqe/property/create,
	// mirroring how PendingJobReviewScreen creates BQE property/client records.
	const [bqeMode, setBqeMode] = useState("existing");
	const [newBqeProperty, setNewBqeProperty] = useState({
		clientName: "",
		clientAddress1: "",
		clientAddress2: "",
		clientCity: "",
		clientState: "",
		clientZip: "",
	});
	const [selectedBqeManager, setSelectedBqeManager] = useState(null);
	const [bqeManagerOptions, setBqeManagerOptions] = useState([]);
	const [bqeManagersLoading, setBqeManagersLoading] = useState(false);
	const [selectedBqeClass, setSelectedBqeClass] = useState(null);
	const [bqeClasses, setBqeClasses] = useState([]);
	const [bqeClassesLoading, setBqeClassesLoading] = useState(false);

	// BQE Client — only relevant while creating a new BQE property (bqeMode === "create").
	// "existing" searches for and reuses a real BQE client; "none" creates the property
	// without linking a client; "create" creates a brand-new client from the fields below.
	const [bqeClientMode, setBqeClientMode] = useState("existing");
	const [bqeClientOptions, setBqeClientOptions] = useState([]);
	const [bqeClientSearchLoading, setBqeClientSearchLoading] = useState(false);
	const [bqeClientSearchValue, setBqeClientSearchValue] = useState("");
	const [bqeClientRecord, setBqeClientRecord] = useState(null);

	// Other Inputs (General Inputs)
	const [dropboxFilePath, setDropboxFilePath] = useState("");
	// "existing" browses/selects an existing Dropbox folder (original behavior); "none"
	// leaves the record unlinked to any folder; "create" creates a brand-new folder,
	// named after the property, inside whatever parent folder is browsed to.
	const [dropboxMode, setDropboxMode] = useState("existing");
	const [OIC, setOIC] = useState("");

	// CONTEXT
	const employeeID = useContext(UserContext);

	// USE EFFECT
	useEffect(() => {
		// populate fields if editing, or if adding with a prefill (the Project Viewer's
		// "Link Property to Tools" — an existing BQE property that has no Tools record yet)
		if (props.editing) {
			handlePopulateFieldsOnEdit(props.recordToEdit);
		} else if (props.open && props.prefillRecord) {
			handlePopulateFieldsOnEdit(props.prefillRecord);
		} else {
			setLoading(false);
		}
	}, [props.open, props.recordToEdit, props.prefillRecord]);

	// Default the "Create New" parent folder to the selected OIC's root path, so the
	// user doesn't have to browse there manually — they can still browse elsewhere
	// below to override it if this property needs to live somewhere else. Only
	// applies when the field is empty or still holds a previous OIC default —
	// never overwrites a path the user has actually browsed to.
	useEffect(() => {
		if (dropboxMode !== "create" || !OIC) return;
		const nextDefault = OIC_DROPBOX_ROOT_PATHS[OIC] || "";
		setDropboxFilePath((current) => {
			if (current && current !== oicDefaultedDropboxPathRef.current) {
				return current;
			}
			oicDefaultedDropboxPathRef.current = nextDefault;
			return nextDefault;
		});
	}, [dropboxMode, OIC]);

	// INPUT HANDLERS

	const bqePropertyNameSelected = (event, newValue) => {
		if (newValue && newValue.displayName) {
			// if a deal name is selected and the user didn't just clear the autocomplete
			setBqePropertyRecord(newValue);
			setBqePropertySearchValue(newValue.displayName);
			setBqePropertyDetailedViewOpen(true);
		} else {
			// if the user cleared the autocomplete — null (not {}), so the
			// "was a BQE property picked" checks elsewhere treat this as unset
			setBqePropertyRecord(null);
			setBqePropertySearchValue("");
			setBqePropertyDetailedViewOpen(false);

			setBqePropertyOptions([]);
		}
	};

	const hubSpotPropertySearchByValueSelected = (event, newValue) => {
		// if the searchByValue is selected and the user didn't just clear the autocomplete
		if (newValue && newValue.properties) {
			setHubSpotPropertyRecord(newValue);
		} else {
			// if the user cleared the autocomplete (still triggers as removing the value is technically a selection event)
			setHubSpotPropertyRecord(null);
			setHubSpotPropertyOptions([]);
		}
	};

	const hubspotSearchExistingClicked = () => {
		setHubspotMode("search");
		setNewHubspotProperty({ name: "", address1: "", address2: "", city: "", state: "", zip: "" });
	};

	const hubspotCreateNewClicked = () => {
		setHubspotMode("create");
		setHubSpotPropertyRecord(null);
		setHubSpotPropertySearchByValue("");
		setHubSpotPropertyOptions([]);
	};

	const handleNewHubspotPropertyChange = (event) => {
		const { name, value } = event.target;
		setNewHubspotProperty((current) => ({ ...current, [name]: value }));
	};

	// GENERAL INPUT HANDLERS

	const handleDropboxFilePathChange = (event) => {
		setDropboxFilePath(event.target.value);
	};

	const handleOICChange = (event) => {
		setOIC(event.target.value);
	};

	// BQE PROPERTY MODE HANDLERS

	const resetNewBqePropertyFields = () => {
		setNewBqeProperty({
			clientName: "",
			clientAddress1: "",
			clientAddress2: "",
			clientCity: "",
			clientState: "",
			clientZip: "",
		});
		setSelectedBqeManager(null);
		setSelectedBqeClass(null);

		setBqeClientMode("existing");
		setBqeClientOptions([]);
		setBqeClientSearchLoading(false);
		setBqeClientSearchValue("");
		setBqeClientRecord(null);
	};

	const fetchBqeManagerOptions = async () => {
		if (bqeManagerOptions.length > 0) return;
		setBqeManagersLoading(true);
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/employees/all/active"
			);
			setBqeManagerOptions((res.data || []).filter((employee) => employee.bqe_id));
		} catch (error) {
			console.log("Error fetching employees for BQE manager picker: ", error);
			setBqeManagerOptions([]);
		}
		setBqeManagersLoading(false);
	};

	const fetchBqeClasses = async () => {
		if (bqeClasses.length > 0) return;
		setBqeClassesLoading(true);
		try {
			const res = await getAPICall(instance, accounts[0], "/api/bqe/class/all");
			setBqeClasses(Array.isArray(res.data) ? res.data : res.data?.items || []);
		} catch (error) {
			console.log("Error fetching BQE classes: ", error);
			setBqeClasses([]);
		}
		setBqeClassesLoading(false);
	};

	// BQE CLIENT MODE HANDLERS (only relevant while bqeMode === "create")

	const bqeClientExistingClicked = () => {
		setBqeClientMode("existing");
		setNewBqeProperty((current) => ({
			...current,
			clientName: "",
			clientAddress1: "",
			clientAddress2: "",
			clientCity: "",
			clientState: "",
			clientZip: "",
		}));
	};

	const bqeClientNoneClicked = () => {
		setBqeClientMode("none");
		setBqeClientRecord(null);
		setBqeClientSearchValue("");
		setBqeClientOptions([]);
		setNewBqeProperty((current) => ({
			...current,
			clientName: "",
			clientAddress1: "",
			clientAddress2: "",
			clientCity: "",
			clientState: "",
			clientZip: "",
		}));
	};

	const bqeClientCreateClicked = () => {
		setBqeClientMode("create");
		setBqeClientRecord(null);
		setBqeClientSearchValue("");
		setBqeClientOptions([]);
	};

	const bqeClientSelected = (event, newValue) => {
		if (newValue && newValue.id) {
			setBqeClientRecord(newValue);
			setBqeClientSearchValue(newValue.name || newValue.displayName || "");
		} else {
			setBqeClientRecord(null);
			setBqeClientSearchValue("");
			setBqeClientOptions([]);
		}
	};

	const bqeClientSearchByNameChanged = useMemo(
		() =>
			debounce(async (event) => {
				setBqeClientSearchLoading(true);
				const textLength = event.target.value.length;

				// if the current text length is less than 3, we will get too many results, so return
				if (textLength < 3) {
					setBqeClientSearchLoading(false);
					setBqeClientOptions([]);
					return;
				}

				try {
					const res = await getAPICall(
						instance,
						accounts[0],
						"/api/bqe/client/byName",
						{ clientName: event.target.value }
					);
					setBqeClientOptions(Array.isArray(res.data) ? res.data : []);
				} catch (error) {
					console.log("Error searching BQE clients: ", error);
					setBqeClientOptions([]);
				}
				setBqeClientSearchLoading(false);
			}, 500),
		[]
	);

	const bqeExistingClicked = () => {
		setBqeMode("existing");
		resetNewBqePropertyFields();
	};

	const bqeNoneClicked = () => {
		setBqeMode("none");
		setBqePropertyRecord(null);
		setBqePropertySearchValue("");
		setBqePropertyOptions([]);
		setBqePropertyDetailedViewOpen(false);
		resetNewBqePropertyFields();
	};

	const bqeCreateClicked = () => {
		setBqeMode("create");
		setBqePropertyRecord(null);
		setBqePropertySearchValue("");
		setBqePropertyOptions([]);
		setBqePropertyDetailedViewOpen(false);
		fetchBqeManagerOptions();
		fetchBqeClasses();
	};

	const handleNewBqePropertyChange = (event) => {
		const { name, value } = event.target;
		setNewBqeProperty((current) => ({ ...current, [name]: value }));
	};

	// DROPBOX MODE HANDLERS

	const dropboxExistingClicked = () => {
		setDropboxMode("existing");
	};

	const dropboxNoneClicked = () => {
		setDropboxMode("none");
		setDropboxFilePath("");
	};

	const dropboxCreateClicked = () => {
		setDropboxMode("create");
	};

	// API FUNCTIONS

	// populate fields on edit
	const handlePopulateFieldsOnEdit = async (record) => {
		setLoading(true);
		setHubspotMode("search");
		setBqeMode("existing");
		resetNewBqePropertyFields();
		setDropboxMode("existing");
		if (record.bqe_id) {
			await fetchBQEPropertyRecord(record.bqe_id);
		}
		if (record.hubspot_id) {
			await fetchHubSpotPropertyRecord(record.hubspot_id);
		}
		setDropboxFilePath(record.dropbox_file_path || "");
		setOIC(record.oic || "");
		setLoading(false);
	};

	const fetchBQEPropertyRecord = async (bqeID) => {
		setBqePropertySearchLoading(true);
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/bqe/property/byID",
				{
					id: bqeID,
				}
			);
			setBqePropertyRecord(res.data);
			setBqePropertyDetailedViewOpen(true);
		} catch (error) {
			console.log("Error in fetchBQEPropertyRecord: ", error);
		}
		setBqePropertySearchLoading(false);
	};

	const fetchHubSpotPropertyRecord = async (hubSpotID) => {
		setHubSpotPropertySearchLoading(true);
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/hubspot/getPropertyFromPropertyID",
				{
					propertyID: hubSpotID,
				}
			);
			if (res.data) {
				hubSpotPropertySearchByValueChanged({
					target: { value: res.data.properties.property_name },
				});
				hubSpotPropertySearchByValueSelected(null, res.data);
				setHubSpotPropertySearchByValue(
					res.data.properties.property_name
				);
			} else {
				setAlertMessage(
					"Error fetching property records in HubSpot. Record may not be associated with a property in HubSpot."
				);
				setAlertOpen(true);
			}
		} catch (error) {
			console.log("Error in fetchHubSpotPropertyRecord: ", error);
		}
		setHubSpotPropertySearchLoading(false);
	};

	const bqePropertyNameChanged = useMemo(
		() =>
			debounce(async (event) => {
				setBqePropertySearchLoading(true);
				const textLength = event.target.value.length;

				// if the current text length is less than 3, we will get too many results, so return
				if (textLength < 3) {
					setBqePropertySearchLoading(false);
					setBqePropertyOptions([]);
					return;
				}

				try {
					const res = await getAPICall(
						instance,
						accounts[0],
						"/api/bqe/property/byName",
						{
							propertyName: event.target.value,
						}
					);

					setBqePropertyOptions(
						res.data == ""
							? []
							: res.data.filter(
									(option) =>
										!option.displayName.endsWith(
											"(deleted)"
										)
							  )
					);
				} catch (error) {
					setBqePropertyOptions([]);
					console.log("Error in bqepropertyNameChanged: ", error);
				}
				setBqePropertySearchLoading(false);
			}, 500),
		[]
	);

	const hubSpotPropertySearchByValueChanged = useMemo(
		() =>
			debounce(async (event) => {
				setHubSpotPropertySearchLoading(true);
				const textLength = event.target.value.length;

				// if the current text length is less than 3, we will get too many results, so return
				if (textLength < 3) {
					setHubSpotPropertySearchLoading(false);
					setHubSpotPropertyOptions([]);
					return;
				}

				try {
					const res = await getAPICall(
						instance,
						accounts[0],
						"/api/hubspot/search/properties",
						{
							propertyName: event.target.value,
						}
					);
					setHubSpotPropertyOptions(res.data);
				} catch (error) {
					console.log(error);
					setAlertMessage(
						"Error fetching property records in HubSpot. Refresh your browser and try again. If the problem persists, contact your system administrator."
					);
					setAlertOpen(true);
				}
				setHubSpotPropertySearchLoading(false);
			}, 500),
		[]
	); // 500 represents the 500ms delay sent to the debounce function below

	// creates a brand-new HubSpot property record (custom object 2-11693811)
	// from the "Create New" fields, instead of reusing an existing one
	const createHubspotPropertyRecord = async () => {
		const res = await postAPICall(
			instance,
			accounts[0],
			"/api/hubspot/addProperty",
			{
				propertyName: newHubspotProperty.name,
				propertyAddress1: newHubspotProperty.address1,
				propertyAddress2: newHubspotProperty.address2,
				propertyCity: newHubspotProperty.city,
				propertyState: newHubspotProperty.state,
				propertyZip: newHubspotProperty.zip,
			}
		);
		return res.data;
	};

	// resolves the property fields (name/address/city/state/zip) to send to BQE,
	// reusing whichever HubSpot property fields are currently active — search or create
	const getEffectivePropertyFields = () => {
		if (hubspotMode === "create") {
			return {
				name: newHubspotProperty.name,
				address1: newHubspotProperty.address1,
				address2: newHubspotProperty.address2,
				city: newHubspotProperty.city,
				state: newHubspotProperty.state,
				zip: newHubspotProperty.zip,
			};
		}
		return {
			name: hubSpotPropertyRecord?.properties?.property_name || "",
			address1: hubSpotPropertyRecord?.properties?.property_address_1 || "",
			address2: hubSpotPropertyRecord?.properties?.property_address_2 || "",
			city: hubSpotPropertyRecord?.properties?.property_city || "",
			state: hubSpotPropertyRecord?.properties?.property_state || "",
			zip: hubSpotPropertyRecord?.properties?.property_zip_code || "",
		};
	};

	// Resolves the BQE client to link the new property to: an explicitly selected
	// existing client, no client at all, or a brand-new client created from the
	// "Create New" fields.
	const resolveBqeClientId = async () => {
		if (bqeClientMode === "none") return null;
		if (bqeClientMode === "existing") return bqeClientRecord?.id || null;

		const createRes = await postAPICall(
			instance,
			accounts[0],
			"/api/bqe/client/create",
			{
				clientName: newBqeProperty.clientName,
				addressLine1: newBqeProperty.clientAddress1,
				addressLine2: newBqeProperty.clientAddress2,
				city: newBqeProperty.clientCity,
				state: newBqeProperty.clientState,
				zip: newBqeProperty.clientZip,
			}
		);
		if (!createRes.data || !createRes.data.id) {
			throw new Error("Failed to create BQE client record");
		}
		return createRes.data.id;
	};

	// Generates a unique property code (for BQE), retrying up to 10 times against the
	// local tools DB — mirrors PendingJobReviewScreen's generatePropertyCode.
	// Code format: PP-XXXXX (5 uppercase pseudorandom alphanumeric characters)
	const generateUniqueBqePropertyCode = async () => {
		let propertyCode = `PP-${generateAlphanumericCode()}`;

		for (let attempt = 0; attempt < 10; attempt++) {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/jobs/property/byBQECode",
				{ bqeCode: propertyCode }
			);

			if (res.data.length === 0) return propertyCode;

			propertyCode = `PP-${generateAlphanumericCode()}`;
		}

		throw new Error("Failed to generate a unique BQE property code");
	};

	// Sets the OIC custom field on the newly-created BQE property (separate from the
	// class/classId fields already sent to /property/create) — mirrors
	// PendingJobReviewScreen's associateOICCustomField. Best-effort: a failure here
	// shouldn't undo the property record that was already successfully created.
	const associateBqeOicCustomField = async (bqeProjectId) => {
		try {
			const bqeOICValue = BQE_OIC_CUSTOM_FIELD_VALUES[OIC] || BQE_OIC_CUSTOM_FIELD_VALUES.WA;
			await postAPICall(instance, accounts[0], "/api/bqe/oic/assign", {
				bqeProjectId,
				bqeOICValue,
			});
		} catch (error) {
			console.log("Error associating BQE OIC custom field: ", error);
		}
	};

	// creates a brand-new BQE property record (and, if needed, its BQE client) from
	// the "Create New" fields, instead of reusing an existing one
	const createBqePropertyRecord = async () => {
		const clientId = await resolveBqeClientId();
		const propertyFields = getEffectivePropertyFields();
		const propertyCode = await generateUniqueBqePropertyCode();

		const res = await postAPICall(
			instance,
			accounts[0],
			"/api/bqe/property/create",
			{
				clientId,
				propertyCode,
				managerId: selectedBqeManager?.bqe_id,
				propertyName: propertyFields.name,
				addressLine1: propertyFields.address1,
				addressLine2: propertyFields.address2,
				city: propertyFields.city,
				state: propertyFields.state,
				zip: propertyFields.zip,
				oic: OIC,
				classId: selectedBqeClass?.id,
			}
		);
		if (!res.data || !res.data.id) {
			throw new Error("Failed to create BQE property record");
		}

		await associateBqeOicCustomField(res.data.id);

		return res.data;
	};

	// the property's name is the folder name — never freely typed, so a new
	// property's folder always matches its HubSpot/Tools name
	const getNewPropertyFolderName = () =>
		hubspotMode === "create"
			? newHubspotProperty.name
			: hubSpotPropertyRecord?.properties?.property_name;

	// resolves the dropbox path to save for this record: the browsed-to existing
	// folder, null (no folder linked), or a brand-new folder named after the property,
	// created under whatever parent folder was browsed to
	const resolveDropboxPath = async () => {
		if (dropboxMode === "none") return null;
		if (dropboxMode === "existing") return dropboxFilePath || null;

		const parent = dropboxFilePath || "";
		const newPath = `${parent}/${getNewPropertyFolderName()}`.replace(/\/{2,}/g, "/");

		const res = await postAPICall(
			instance,
			accounts[0],
			"/api/dropbox/create/folder",
			{ path: newPath }
		);
		if (!res.data || res.data.error) {
			throw new Error("Failed to create Dropbox folder");
		}
		return newPath;
	};

	// adds record to tools db
	const handleAddRecord = async () => {
		setLoading(true);
		try {
			const hubspotRecord =
				hubspotMode === "create"
					? await createHubspotPropertyRecord()
					: hubSpotPropertyRecord;

			const bqePropertyForSave =
				bqeMode === "create"
					? await createBqePropertyRecord()
					: bqeMode === "existing"
						? bqePropertyRecord
						: null;

			const resolvedDropboxPath = await resolveDropboxPath();

			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/jobs/property/create",
				{
					name: hubspotRecord.properties.property_name,
					addressLine1:
						hubspotRecord.properties.property_address_1,
					addressLine2:
						hubspotRecord.properties.property_address_2,
					city: hubspotRecord.properties.property_city,
					state: hubspotRecord.properties.property_state,
					zip: hubspotRecord.properties.property_zip_code,
					dropboxFilePath: resolvedDropboxPath,
					bqeID: bqePropertyForSave ? bqePropertyForSave.id : null,
					bqeCode: bqePropertyForSave ? bqePropertyForSave.code : null,
					oic: OIC,
					createdBy: employeeID,
					hubspotID: hubspotRecord.id,
				}
			);
			if (isDbErrorResponse(res.data)) {
				throw new Error(
					res.data.sqlMessage || res.data.code || "Failed to save property record"
				);
			}
			setAlertMessage("Record added successfully");
			setAlertOpen(true);
			setLoading(false);
			return true;
		} catch (error) {
			console.log(error);
			setAlertMessage(
				error?.message ||
					(hubspotMode === "create"
						? "Error creating HubSpot property record"
						: "Error adding record")
			);
			setAlertOpen(true);
			setLoading(false);
			return false;
		}
	};

	const handleUpdateRecord = async () => {
		setLoading(true);
		try {
			const bqePropertyForSave =
				bqeMode === "create"
					? await createBqePropertyRecord()
					: bqeMode === "existing"
						? bqePropertyRecord
						: null;

			const resolvedDropboxPath = await resolveDropboxPath();

			const res = await putAPICall(
				instance,
				accounts[0],
				"/api/jobs/property/update",
				{
					id: props.recordToEdit.id,
					name: hubSpotPropertyRecord.properties.property_name,
					addressLine1:
						hubSpotPropertyRecord.properties.property_address_1,
					addressLine2:
						hubSpotPropertyRecord.properties.property_address_2,
					city: hubSpotPropertyRecord.properties.property_city,
					state: hubSpotPropertyRecord.properties.property_state,
					zip: hubSpotPropertyRecord.properties.property_zip_code,
					dropboxFilePath: resolvedDropboxPath,
					bqeID: bqePropertyForSave ? bqePropertyForSave.id : null,
					bqeCode: bqePropertyForSave ? bqePropertyForSave.code : null,
					oic: OIC,
					modifiedBy: employeeID,
					hubspotID: hubSpotPropertyRecord.id,
				}
			);
			if (isDbErrorResponse(res.data)) {
				throw new Error(
					res.data.sqlMessage || res.data.code || "Failed to save property record"
				);
			}
			setAlertMessage("Record updated successfully");
			setAlertOpen(true);
			setLoading(false);
			return true;
		} catch (error) {
			console.log(error);
			setAlertMessage(error?.message || "Error updating record");
			setAlertOpen(true);
			setLoading(false);
			return false;
		}
	};

	// HELPER FUNCTIONS

	const handleSubmitClick = async () => {
		if (!validateInputFields()) return;
		setLoading(true);
		// if editing, update record, else add record
		const succeeded = props.editing
			? await handleUpdateRecord()
			: await handleAddRecord();

		// Leave the dialog open with the error alert visible on failure —
		// closing here would wipe the alert (via resetInputFields/onClose)
		// before the user ever saw why the save didn't go through.
		if (!succeeded) return;

		resetInputFields();
		setAlertMessage("");
		setAlertOpen(false);
		setLoading(false);
		props.onClose();
	};

	const handleCancelClick = () => {
		resetInputFields();
		setAlertMessage("");
		setAlertOpen(false);
		props.onCloseNoRefresh();
	};

	const resetInputFields = () => {
		setLoading(false);
		setAlertOpen(false);
		setAlertMessage("");
		//reset refs
		hubSpotPropertySearchByValueRef.current = null;
		bqePropertyNameRef.current = null;

		setHubSpotPropertySearchByValue("");
		setHubSpotPropertyOptions([]);
		setHubSpotPropertySearchLoading(false);

		setBqePropertyOptions([]);
		setBqePropertyDetailedViewOpen(false);
		setBqePropertySearchValue("");
		setBqePropertySearchLoading(false);

		setDropboxFilePath("");
		setOIC("");

		setHubSpotPropertyRecord(null);

		setBqePropertyRecord(null);

		setHubspotMode("search");
		setNewHubspotProperty({ name: "", address1: "", address2: "", city: "", state: "", zip: "" });

		setBqeMode("existing");
		resetNewBqePropertyFields();

		setDropboxMode("existing");
	};

	const validateInputFields = () => {
		if (OIC === "") {
			setAlertMessage("Please Fill Out All Fields");
			setAlertOpen(true);
			return false;
		}
		// BQE Property and Dropbox File Path are optional — a brand-new
		// property may not be connected to a project yet, so it won't have
		// either of those set up. They can be filled in later once it is.
		if (hubspotMode === "search") {
			if (!hubSpotPropertyRecord) {
				setAlertMessage("Please Select a HubSpot Property");
				setAlertOpen(true);
				return false;
			}
		} else if (
			!newHubspotProperty.name ||
			!newHubspotProperty.address1 ||
			!newHubspotProperty.city ||
			!newHubspotProperty.state ||
			!newHubspotProperty.zip
		) {
			setAlertMessage(
				"Please Fill Out the New HubSpot Property's Name, Address, City, State, and Zip"
			);
			setAlertOpen(true);
			return false;
		}

		// TODO: eventually OIC should be a dropdown based on offices
		if (OIC !== "OR" && OIC !== "WA" && OIC !== "UT") {
			setAlertMessage("OIC must be OR, WA, or UT");
			setAlertOpen(true);
			return false;
		}

		if (bqeMode === "create") {
			if (!selectedBqeManager || !selectedBqeClass) {
				setAlertMessage(
					"Please select a Manager and Class for the new BQE property"
				);
				setAlertOpen(true);
				return false;
			}
			if (bqeClientMode === "existing" && !bqeClientRecord) {
				setAlertMessage("Please select a BQE client");
				setAlertOpen(true);
				return false;
			}
			if (
				bqeClientMode === "create" &&
				(!newBqeProperty.clientName ||
					!newBqeProperty.clientAddress1 ||
					!newBqeProperty.clientCity ||
					!newBqeProperty.clientState ||
					!newBqeProperty.clientZip)
			) {
				setAlertMessage(
					"Please fill out the new BQE client's Name, Address, City, State, and Zip"
				);
				setAlertOpen(true);
				return false;
			}
		}

		if (dropboxMode === "create" && !getNewPropertyFolderName()) {
			setAlertMessage(
				"A property name is required before its Dropbox folder can be created"
			);
			setAlertOpen(true);
			return false;
		}

		return true;
	};

	// RENDER
	return (
		<Dialog
			id="PropertyConsoleEditAddDialog"
			open={props.open}
			maxWidth="xl"
			fullWidth
			onClose={handleCancelClick}
		>
			<DialogTitle id="PropertyConsoleEditAddDialogTitle">
				{/* edit / add */}
				{props.editing
					? "Edit Property Record"
					: props.prefillRecord
					? "Link Property to Tools"
					: "Add Property Record"}
			</DialogTitle>
			{loading && (
				<div className="propertyConsoleLoadingSpinner">
					<CircularProgress />
				</div>
			)}
			{!loading && (
				<DialogContent id="PropertyConsoleEditAddDialogContent">
					{alertOpen && (
						<Alert
							severity="warning"
							action={
								<IconButton
									aria-label="close"
									color="inherit"
									size="small"
									onClick={() => {
										setAlertOpen(false);
									}}
								>
									<CloseIcon />
								</IconButton>
							}
						>
							{alertMessage}
						</Alert>
					)}

					{/* HubSpot Property */}
					<div className="dialogSubtitle">
						{props.editing ? (
							"Search Existing HubSpot Property Record"
						) : (
							<div className="dialogSubtitleWithActions">
								<span>HubSpot Property Record</span>
								<ButtonGroup size="small">
									<Button
										sx={{
											"&.Mui-disabled": {
												background: "#184C93",
												color: "#ffffff",
											},
										}}
										variant={
											hubspotMode === "search"
												? "contained"
												: "outlined"
										}
										onClick={hubspotSearchExistingClicked}
										disabled={hubspotMode === "search"}
									>
										Search Existing
									</Button>
									<Button
										sx={{
											"&.Mui-disabled": {
												background: "#184C93",
												color: "#ffffff",
											},
										}}
										variant={
											hubspotMode === "create"
												? "contained"
												: "outlined"
										}
										onClick={hubspotCreateNewClicked}
										disabled={hubspotMode === "create"}
									>
										Create New
									</Button>
								</ButtonGroup>
							</div>
						)}
					</div>
					<FormControl sx={{ flex: 1 }}>
						<div className="FormBlock">
							{hubspotMode === "search" && (
								<div className="FormRow">
									<Autocomplete
										sx={{ flexGrow: 1 }}
										options={hubSpotPropertyOptions}
										ref={hubSpotPropertySearchByValueRef}
										getOptionLabel={(option) =>
											option.properties
												? option.properties.property_name
												: option
										}
										value={
											hubSpotPropertyOptions.find(
												(option) =>
													option.properties
														.property_name ===
													hubSpotPropertySearchByValue
											) || null
										}
										filterSelectedOptions
										onChange={
											hubSpotPropertySearchByValueSelected
										}
										clearOnBlur={false}
										renderInput={(params) => (
											<TextField
												{...params}
												label="HubSpot Property Name"
												placeholder="HubSpot Property Name"
												value={
													hubSpotPropertySearchByValue ||
													""
												}
												onKeyUp={
													hubSpotPropertySearchByValueChanged
												}
												InputProps={{
													...params.InputProps,
													endAdornment: (
														<>
															{hubSpotPropertySearchLoading ? (
																<CircularProgress
																	color="inherit"
																	size={20}
																/>
															) : null}
															{
																params.InputProps
																	.endAdornment
															}
														</>
													),
												}}
											/>
										)}
									/>
								</div>
							)}
							<div className="FormRow">
								<TextField
									sx={{ flexGrow: 1 }}
									disabled={hubspotMode === "search"}
									label="Property Name"
									name="name"
									onChange={handleNewHubspotPropertyChange}
									value={
										hubspotMode === "search"
											? hubSpotPropertyRecord
												? hubSpotPropertyRecord
														.properties
														.property_name || ""
												: ""
											: newHubspotProperty.name
									}
								/>
							</div>
							<div className="FormRow">
								<TextField
									sx={{ flexGrow: 1 }}
									disabled={hubspotMode === "search"}
									label="Address Line 1"
									name="address1"
									onChange={handleNewHubspotPropertyChange}
									value={
										hubspotMode === "search"
											? hubSpotPropertyRecord
												? hubSpotPropertyRecord
														.properties
														.property_address_1 || ""
												: ""
											: newHubspotProperty.address1
									}
								/>
								<TextField
									sx={{ flexGrow: 1 }}
									disabled={hubspotMode === "search"}
									label="Address Line 2"
									name="address2"
									onChange={handleNewHubspotPropertyChange}
									value={
										hubspotMode === "search"
											? hubSpotPropertyRecord
												? hubSpotPropertyRecord
														.properties
														.property_address_2 || ""
												: ""
											: newHubspotProperty.address2
									}
								/>
							</div>
							<div className="FormRow">
								<TextField
									sx={{ flexGrow: 1 }}
									disabled={hubspotMode === "search"}
									label="City"
									name="city"
									onChange={handleNewHubspotPropertyChange}
									value={
										hubspotMode === "search"
											? hubSpotPropertyRecord
												? hubSpotPropertyRecord
														.properties
														.property_city || ""
												: ""
											: newHubspotProperty.city
									}
								/>
								<FormControl sx={{ flexGrow: 1 }}>
									<InputLabel id="propertyStateLabel">
										State
									</InputLabel>
									<Select
										labelId="hubSpotPropertyStateLabel"
										label="State"
										disabled={hubspotMode === "search"}
										name="state"
										onChange={handleNewHubspotPropertyChange}
										value={
											hubspotMode === "search"
												? hubSpotPropertyRecord
													? hubSpotPropertyRecord
															.properties
															.property_state || ""
													: ""
												: newHubspotProperty.state
										}
									>
										<MenuItem value="Washington">
											Washington
										</MenuItem>
										<MenuItem value="Oregon">
											Oregon
										</MenuItem>
										<MenuItem value="Utah">Utah</MenuItem>
										<MenuItem value="Idaho">Idaho</MenuItem>
									</Select>
								</FormControl>
								<TextField
									sx={{ flexGrow: 1 }}
									disabled={hubspotMode === "search"}
									label="ZIP"
									name="zip"
									onChange={handleNewHubspotPropertyChange}
									value={
										hubspotMode === "search"
											? hubSpotPropertyRecord
												? hubSpotPropertyRecord
														.properties
														.property_zip_code || ""
												: ""
											: newHubspotProperty.zip
									}
								/>
							</div>
						</div>
						<FormLabel>
							{hubspotMode === "search"
								? "These values will be used to populate the Tools Record."
								: "A new HubSpot property record will be created with these values when this record is saved."}
						</FormLabel>
					</FormControl>

					<Divider />

					<div className="dialogSubtitle">OIC</div>

					{/* TODO: fix OIC association and do a drop down to pick one instead of textbox */}
					<div className="FormBlock">
						<div className="FormRow">
							<FormControl sx={{ flex: 1 }}>
								<InputLabel id="OIC">OIC</InputLabel>
								<Select
									value={OIC ? OIC : ""}
									onChange={handleOICChange}
									label="OIC"
									labelId="OIC"
								>
									<MenuItem value={"OR"}>OR</MenuItem>
									<MenuItem value={"WA"}>WA</MenuItem>
									<MenuItem value={"UT"}>UT</MenuItem>
								</Select>
							</FormControl>
						</div>
					</div>

					<Divider />
					<div className="dialogSubtitleWithActions">
						<span>BQE Property Record</span>
						<ButtonGroup size="small">
							<Button
								sx={{
									"&.Mui-disabled": {
										background: "#184C93",
										color: "#ffffff",
									},
								}}
								variant={bqeMode === "existing" ? "contained" : "outlined"}
								onClick={bqeExistingClicked}
								disabled={bqeMode === "existing"}
							>
								Search Existing
							</Button>
							<Button
								sx={{
									"&.Mui-disabled": {
										background: "#184C93",
										color: "#ffffff",
									},
								}}
								variant={bqeMode === "none" ? "contained" : "outlined"}
								onClick={bqeNoneClicked}
								disabled={bqeMode === "none"}
							>
								None
							</Button>
							<Button
								sx={{
									"&.Mui-disabled": {
										background: "#184C93",
										color: "#ffffff",
									},
								}}
								variant={bqeMode === "create" ? "contained" : "outlined"}
								onClick={bqeCreateClicked}
								disabled={bqeMode === "create"}
							>
								Create New
							</Button>
						</ButtonGroup>
					</div>

					{/* BQE PROPERTY */}
					{bqeMode === "existing" && (
						<FormControl sx={{ flex: 1 }}>
							<div className="FormBlock">
								<div className="FormRow">
									<Autocomplete
										sx={{ flexGrow: 1 }}
										options={bqePropertyOptions}
										ref={bqePropertyNameRef}
										getOptionLabel={(option) =>
											option.displayName ?? option
										}
										filterSelectedOptions
										value={
											bqePropertyRecord &&
											Object.keys(bqePropertyRecord).length >
												0
												? bqePropertyRecord
												: ""
										}
										onChange={bqePropertyNameSelected}
										clearOnBlur={false}
										loading={loading}
										renderInput={(params) => (
											<TextField
												{...params}
												label="BQE Property Name"
												placeholder="BQE Property Name"
												value={bqePropertySearchValue || ""}
												onKeyUp={bqePropertyNameChanged}
												InputProps={{
													...params.InputProps,
													endAdornment: (
														<>
															{bqePropertySearchLoading ? (
																<CircularProgress
																	color="inherit"
																	size={20}
																/>
															) : null}
															{
																params.InputProps
																	.endAdornment
															}
														</>
													),
												}}
											/>
										)}
									/>
								</div>
							</div>

							<Collapse
								in={
									bqePropertyDetailedViewOpen && bqePropertyRecord
								}
								id="createNewPropertyCollapse"
							>
								<div
									className="FormBlock"
									style={{ marginTop: "1em" }}
								>
									<div className="FormRow">
										<TextField
											sx={{ flex: 1 }}
											disabled
											label="BQE Property Name"
											value={
												bqePropertyRecord
													? bqePropertyRecord?.displayName
													: ""
											}
										/>
									</div>
									<div className="FormRow">
										<TextField
											sx={{ flex: 1 }}
											disabled
											label="Address Line 1"
											value={
												bqePropertyRecord
													? bqePropertyRecord
															?.address?.[0]?.street1
													: ""
											}
										/>
										<TextField
											sx={{ flex: 1 }}
											disabled
											label="Address Line 2"
											value={
												bqePropertyRecord
													? bqePropertyRecord
															?.address?.[0]?.street2
													: ""
											}
										/>
									</div>
									<div className="FormRow">
										<TextField
											sx={{ flex: 1 }}
											disabled
											label="City"
											value={
												bqePropertyRecord
													? bqePropertyRecord
															?.address?.[0]?.city
													: ""
											}
										/>
										<TextField
											sx={{ flex: 1 }}
											disabled
											label="State"
											value={
												bqePropertyRecord
													? bqePropertyRecord
															?.address?.[0]?.state
													: ""
											}
										/>
										<TextField
											sx={{ flex: 1 }}
											disabled
											label="Zip"
											value={
												bqePropertyRecord
													? bqePropertyRecord
															?.address?.[0]?.zip
													: ""
											}
										/>
									</div>
								</div>
							</Collapse>
						</FormControl>
					)}

					{bqeMode === "none" && (
						<FormLabel>
							No BQE property will be linked to this record.
						</FormLabel>
					)}

					{bqeMode === "create" && (
						<FormControl sx={{ flex: 1 }}>
							<div className="FormBlock">
								<div className="FormRow">
									<Autocomplete
										sx={{ flex: 1 }}
										options={bqeManagerOptions}
										getOptionLabel={(option) =>
											`${option.first_name || ""} ${option.last_name || ""}`.trim()
										}
										value={selectedBqeManager}
										onChange={(event, newValue) => setSelectedBqeManager(newValue)}
										loading={bqeManagersLoading}
										renderInput={(params) => (
											<TextField {...params} label="Manager" placeholder="Select a manager" />
										)}
									/>
									<Autocomplete
										sx={{ flex: 1 }}
										options={bqeClasses}
										getOptionLabel={(option) =>
											option.name || option.displayName || String(option.id ?? "")
										}
										value={selectedBqeClass}
										onChange={(event, newValue) => setSelectedBqeClass(newValue)}
										loading={bqeClassesLoading}
										renderInput={(params) => (
											<TextField {...params} label="Class" placeholder="Select a class" />
										)}
									/>
								</div>
							</div>

							<Divider sx={{ marginTop: "1em", marginBottom: "1em" }} />
							<div className="dialogSubtitleWithActions">
								<span>BQE Client</span>
								<ButtonGroup size="small">
									<Button
										sx={{
											"&.Mui-disabled": {
												background: "#184C93",
												color: "#ffffff",
											},
										}}
										variant={bqeClientMode === "existing" ? "contained" : "outlined"}
										onClick={bqeClientExistingClicked}
										disabled={bqeClientMode === "existing"}
									>
										Search Existing
									</Button>
									<Button
										sx={{
											"&.Mui-disabled": {
												background: "#184C93",
												color: "#ffffff",
											},
										}}
										variant={bqeClientMode === "none" ? "contained" : "outlined"}
										onClick={bqeClientNoneClicked}
										disabled={bqeClientMode === "none"}
									>
										None
									</Button>
									<Button
										sx={{
											"&.Mui-disabled": {
												background: "#184C93",
												color: "#ffffff",
											},
										}}
										variant={bqeClientMode === "create" ? "contained" : "outlined"}
										onClick={bqeClientCreateClicked}
										disabled={bqeClientMode === "create"}
									>
										Create New
									</Button>
								</ButtonGroup>
							</div>

							{bqeClientMode === "existing" && (
								<div className="FormBlock" style={{ marginTop: "1em" }}>
									<div className="FormRow">
										<Autocomplete
											sx={{ flexGrow: 1 }}
											options={bqeClientOptions}
											getOptionLabel={(option) =>
												option.name || option.displayName || ""
											}
											value={bqeClientRecord}
											filterSelectedOptions
											onChange={bqeClientSelected}
											clearOnBlur={false}
											renderInput={(params) => (
												<TextField
													{...params}
													label="BQE Client Name"
													placeholder="BQE Client Name"
													value={bqeClientSearchValue}
													onChange={(e) =>
														setBqeClientSearchValue(e.target.value)
													}
													onKeyUp={bqeClientSearchByNameChanged}
													InputProps={{
														...params.InputProps,
														endAdornment: (
															<>
																{bqeClientSearchLoading && (
																	<CircularProgress
																		color="inherit"
																		size={20}
																	/>
																)}
															</>
														),
													}}
												/>
											)}
										/>
									</div>
								</div>
							)}

							{bqeClientMode === "none" && (
								<FormLabel sx={{ marginTop: "1em", display: "block" }}>
									No BQE client will be linked to this property.
								</FormLabel>
							)}

							{bqeClientMode === "create" && (
								<div className="FormBlock" style={{ marginTop: "1em" }}>
									<div className="FormRow">
										<TextField
											sx={{ flex: 1 }}
											label="Client Name"
											name="clientName"
											value={newBqeProperty.clientName}
											onChange={handleNewBqePropertyChange}
										/>
									</div>
									<div className="FormRow">
										<TextField
											sx={{ flex: 1 }}
											label="Client Address Line 1"
											name="clientAddress1"
											value={newBqeProperty.clientAddress1}
											onChange={handleNewBqePropertyChange}
										/>
										<TextField
											sx={{ flex: 1 }}
											label="Client Address Line 2"
											name="clientAddress2"
											value={newBqeProperty.clientAddress2}
											onChange={handleNewBqePropertyChange}
										/>
									</div>
									<div className="FormRow">
										<TextField
											sx={{ flex: 1 }}
											label="Client City"
											name="clientCity"
											value={newBqeProperty.clientCity}
											onChange={handleNewBqePropertyChange}
										/>
										<FormControl sx={{ flex: 1 }}>
											<InputLabel id="bqeClientStateLabel">Client State</InputLabel>
											<Select
												labelId="bqeClientStateLabel"
												label="Client State"
												name="clientState"
												value={newBqeProperty.clientState}
												onChange={handleNewBqePropertyChange}
											>
												<MenuItem value="Washington">Washington</MenuItem>
												<MenuItem value="Oregon">Oregon</MenuItem>
												<MenuItem value="Utah">Utah</MenuItem>
												<MenuItem value="Idaho">Idaho</MenuItem>
											</Select>
										</FormControl>
										<TextField
											sx={{ flex: 1 }}
											label="Client Zip"
											name="clientZip"
											value={newBqeProperty.clientZip}
											onChange={handleNewBqePropertyChange}
										/>
									</div>
								</div>
							)}
						</FormControl>
					)}

					<Divider />

					<div className="dialogSubtitleWithActions">
						<span>Dropbox Folder</span>
						<ButtonGroup size="small">
							<Button
								sx={{
									"&.Mui-disabled": {
										background: "#184C93",
										color: "#ffffff",
									},
								}}
								variant={dropboxMode === "existing" ? "contained" : "outlined"}
								onClick={dropboxExistingClicked}
								disabled={dropboxMode === "existing"}
							>
								Search Existing
							</Button>
							<Button
								sx={{
									"&.Mui-disabled": {
										background: "#184C93",
										color: "#ffffff",
									},
								}}
								variant={dropboxMode === "none" ? "contained" : "outlined"}
								onClick={dropboxNoneClicked}
								disabled={dropboxMode === "none"}
							>
								None
							</Button>
							<Button
								sx={{
									"&.Mui-disabled": {
										background: "#184C93",
										color: "#ffffff",
									},
								}}
								variant={dropboxMode === "create" ? "contained" : "outlined"}
								onClick={dropboxCreateClicked}
								disabled={dropboxMode === "create"}
							>
								Create New
							</Button>
						</ButtonGroup>
					</div>

					{dropboxMode === "existing" && (
						<div className="FormBlock">
							<div className="FormRow">
								<DropboxFileSelector
									filePath={dropboxFilePath}
									setFilePath={setDropboxFilePath}
								/>
							</div>
						</div>
					)}

					{dropboxMode === "none" && (
						<FormLabel>
							No Dropbox folder will be linked to this record.
						</FormLabel>
					)}

					{dropboxMode === "create" && (
						<div className="FormBlock">
							<div className="FormRow">
								<DropboxFileSelector
									filePath={dropboxFilePath}
									setFilePath={setDropboxFilePath}
								/>
							</div>
							<FormLabel>
								New folder path:{" "}
								{`${dropboxFilePath || ""}/${
									getNewPropertyFolderName() || "…"
								}`.replace(/\/{2,}/g, "/")}
							</FormLabel>
						</div>
					)}

					<div className="editPropertyConsoleButtons">
						<Button
							variant="outlined"
							color="error"
							onClick={handleCancelClick}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={handleSubmitClick}
							disabled={loading}
						>
							Submit
						</Button>
					</div>
				</DialogContent>
			)}
		</Dialog>
	);
};

// EXPORT
export default PropertyConsoleEditAddDialog;
